/**
 * Ввод карантина в «деле контура» (волна 1б №646–650, волна 2б).
 *
 * Инспектор карантина вносит обследование с находкой (вид из справочника,
 * часть контура, поражённая площадь в гектарах или доля), предписание и замер;
 * ответственный карантинной службы — решение (красный, снятие, «без красного»)
 * и направление в агроинспекцию. Менеджер вносит «по актам» (решение владельца
 * 07.10); хоким и остальные — только читают, кнопок записи у них нет. Кнопки
 * показываются по подсказке сервера (`can_enter_quarantine`,
 * `can_decide_quarantine`), но право решает сервер: отказ приходит словами.
 *
 * Действия по находке берут ВЫБРАННУЮ находку, а не последнюю: у контура их
 * может быть несколько (на разных частях).
 *
 * Хозяйство здесь ничего не вносит: об обработке оно отчитывается своей
 * работой по предписанию, и это «обработали, ждём контроля», а не устранение.
 */
import { useEffect, useMemo, useState } from 'react';
import { api, ApiError, request, type QuarantineProblems } from '../../../api/client';
import { useAuth } from '../../../auth/context';
import { AWAITING_WORD, CLASS_LONG, isQuarantineClass, type ObjectClass } from './quarantineArea';
import { defineMessages, type Message } from '../../../i18n/define';
import { UNIT_LABELS, useT } from '../../../i18n/hooks';
import { clampToSlice, useSliceDate } from '../../../analysis/useSliceDate';
import { localIsoDate } from '../../../lib/localDate';
import { DatePicker, NumberField, Select, TextField } from '../../ui/forms';
import './quarantineEntry.css';

type Part = { id: number; label: string };
export type EntryFinding = {
  id: number;
  problem_name?: string | null;
  case_state?: string | null;
  awaiting_reason?: string | null;
  object_class?: string | null;
};
type Requirement = { id: number; finding_id: number };

const m = defineMessages({
  dirPests: { id: 'quarantine.entry.direction.pests', defaultMessage: 'вредители', description: 'Пункт списка «Направление» в форме обследования (инспектор): вредители' },
  dirDiseases: { id: 'quarantine.entry.direction.diseases', defaultMessage: 'болезни', description: 'Пункт списка «Направление» в форме обследования (инспектор): болезни растений' },
  dirWeeds: { id: 'quarantine.entry.direction.weeds', defaultMessage: 'сорняки', description: 'Пункт списка «Направление» в форме обследования (инспектор): сорняки' },
  outEliminated: { id: 'quarantine.entry.outcome.eliminated', defaultMessage: 'устранена', description: 'Пункт списка «Итог замера» в форме замера (инспектор): угроза устранена; строчными' },
  outReduced: { id: 'quarantine.entry.outcome.reduced', defaultMessage: 'уменьшилась', description: 'Пункт списка «Итог замера»: угроза уменьшилась; строчными' },
  outUnchanged: { id: 'quarantine.entry.outcome.unchanged', defaultMessage: 'сохранилась', description: 'Пункт списка «Итог замера»: угроза сохранилась; строчными' },
  outIncreased: { id: 'quarantine.entry.outcome.increased', defaultMessage: 'увеличилась', description: 'Пункт списка «Итог замера»: угроза увеличилась; строчными' },
  decSetRed: { id: 'quarantine.entry.decision.setRed', defaultMessage: 'поставить красный (только карантинный объект)', description: 'Пункт списка «Решение» в форме решения (ответственный карантинной службы): поставить красный статус; красный возможен только на карантинный объект' },
  decKeep: { id: 'quarantine.entry.decision.keep', defaultMessage: 'оставить без красного (новый срок)', description: 'Пункт списка «Решение»: оставить без красного статуса, назначить новый срок' },
  decLiftRed: { id: 'quarantine.entry.decision.liftRed', defaultMessage: 'снять красный вручную (исключение: обычно его снимает сам замер «устранена»)', description: 'Пункт списка «Решение»: снять красный статус после замера «устранена»' },
  saveFailed: { id: 'quarantine.entry.saveFailed', defaultMessage: 'Запись не прошла: {reason}', description: 'Сообщение под формами ввода карантина: запись отклонена; {reason} — причина словами сервера' },
  formInspection: { id: 'quarantine.entry.form.inspection', defaultMessage: 'Обследование / находка', description: 'Кнопка-переключатель формы (инспектор, менеджер): внести обследование или находку' },
  formRequirement: { id: 'quarantine.entry.form.requirement', defaultMessage: 'Предписание', description: 'Кнопка-переключатель формы (инспектор, менеджер): выдать предписание по находке' },
  formFollowup: { id: 'quarantine.entry.form.followup', defaultMessage: 'Замер', description: 'Кнопка-переключатель формы (инспектор, менеджер): внести повторный замер; «устранено» подтверждает только он' },
  formDecision: { id: 'quarantine.entry.form.decision', defaultMessage: 'Решение', description: 'Кнопка-переключатель формы (ответственный карантинной службы): решение по красному статусу' },
  formReferral: { id: 'quarantine.entry.form.referral', defaultMessage: 'В агроинспекцию', description: 'Кнопка-переключатель формы (ответственный): направить красную единицу земли в агроинспекцию' },
  byFinding: { id: 'quarantine.entry.byFinding', defaultMessage: 'По находке', description: 'Подпись списка выбора находки, когда у контура их несколько' },
  findingOption: { id: 'quarantine.entry.findingOption', defaultMessage: '{name} · №{id}{awaiting, select, yes { · требует решения} other {}}', description: 'Пункт списка выбора находки; {name} — вид (или «вид не назван»), {id} — номер находки, {awaiting} — yes, если находка ждёт решения' },
  speciesUnnamed: { id: 'quarantine.entry.speciesUnnamed', defaultMessage: 'вид не назван', description: 'Вместо названия вида в находке, когда вид не назван; строчными' },
  date: { id: 'quarantine.entry.date', defaultMessage: 'Дата', description: 'Подпись поля даты в формах ввода карантина' },
  contourPart: { id: 'quarantine.entry.contourPart', defaultMessage: 'Часть контура', description: 'Подпись списка выбора части контура в форме обследования' },
  wholeContour: { id: 'quarantine.entry.wholeContour', defaultMessage: 'контур целиком', description: 'Пункт списка частей контура: весь контур; строчными' },
  speciesField: { id: 'quarantine.entry.speciesField', defaultMessage: 'Вид (справочник)', description: 'Подпись списка выбора вида из справочника в форме обследования' },
  nothingFound: { id: 'quarantine.entry.nothingFound', defaultMessage: 'ничего не найдено', description: 'Пункт списка видов: обследование ничего не нашло; строчными' },
  speciesWithClass: { id: 'quarantine.entry.speciesWithClass', defaultMessage: '{name} — {class}', description: 'Пункт списка видов; {name} — название вида, {class} — класс вида словами' },
  otherSpecies: { id: 'quarantine.entry.otherSpecies', defaultMessage: 'другого вида нет — вписать текстом', description: 'Пункт списка видов: вида нет в справочнике, ввести текстом; строчными' },
  directionField: { id: 'quarantine.entry.directionField', defaultMessage: 'Направление', description: 'Подпись списка «вредители / болезни / сорняки» для вида, вписанного текстом' },
  speciesText: { id: 'quarantine.entry.speciesText', defaultMessage: 'Вид текстом', description: 'Подпись поля ввода названия вида текстом' },
  speciesTextPlaceholder: { id: 'quarantine.entry.speciesTextPlaceholder', defaultMessage: 'например, повилика', description: 'Подсказка в поле «Вид текстом»; пример названия сорняка (повилика)' },
  affectedHa: { id: 'quarantine.entry.affectedHa', defaultMessage: 'Поражено, га', description: 'Подпись поля ввода: поражённая площадь в гектарах' },
  affectedHaPlaceholder: { id: 'quarantine.entry.affectedHaPlaceholder', defaultMessage: 'пусто — по доле или не указана', description: 'Подсказка в поле «Поражено, га»: если пусто, берётся доля или площадь не указана' },
  affectedShare: { id: 'quarantine.entry.affectedShare', defaultMessage: 'или доля, %', description: 'Подпись поля ввода: поражённая доля части в процентах' },
  affectedSharePlaceholder: { id: 'quarantine.entry.affectedSharePlaceholder', defaultMessage: 'доля земли части', description: 'Подсказка в поле «или доля, %»: доля земли части контура' },
  observed: { id: 'quarantine.entry.observed', defaultMessage: 'Что видно', description: 'Подпись поля свободной заметки инспектора: что видно на месте' },
  classNote: { id: 'quarantine.entry.classNote', defaultMessage: 'Класс вида: {class} — красный для него невозможен, статус будет «наблюдение».', description: 'Пояснение в форме обследования, когда выбран некарантинный вид; {class} — класс вида словами' },
  areaNote: { id: 'quarantine.entry.areaNote', defaultMessage: 'Площадь не названа: под сигналом будет вся обследованная единица («часть не указана»).', description: 'Пояснение в форме обследования, когда ни площадь, ни доля не указаны' },
  findingSaved: { id: 'quarantine.entry.findingSaved', defaultMessage: 'Находка записана.', description: 'Сообщение после записи находки' },
  inspectionSaved: { id: 'quarantine.entry.inspectionSaved', defaultMessage: 'Обследование записано: ничего не найдено.', description: 'Сообщение после записи обследования без находки' },
  saving: { id: 'quarantine.entry.saving', defaultMessage: 'Записываем…', description: 'Кнопка записи, пока запись идёт; коротко' },
  save: { id: 'quarantine.entry.save', defaultMessage: 'Записать', description: 'Кнопка отправки формы ввода карантина; коротко' },
  byFindingNote: { id: 'quarantine.entry.byFindingNote', defaultMessage: 'По находке: {name}', description: 'Строка в форме предписания: к какой находке относится предписание; {name} — вид (или «вид не назван»)' },
  method: { id: 'quarantine.entry.method', defaultMessage: 'Метод устранения', description: 'Подпись поля ввода метода устранения в форме предписания' },
  issued: { id: 'quarantine.entry.issued', defaultMessage: 'Выдано', description: 'Подпись поля даты выдачи предписания' },
  deadline: { id: 'quarantine.entry.deadline', defaultMessage: 'Срок', description: 'Подпись поля даты: срок устранения по предписанию' },
  requirementSaved: { id: 'quarantine.entry.requirementSaved', defaultMessage: 'Предписание записано. Работа выдана хозяйству через агроцикл поля; если на дату землепользователя нет — случай «адресат неизвестен».', description: 'Сообщение после записи предписания: что произошло с работой хозяйства' },
  outcomeField: { id: 'quarantine.entry.outcomeField', defaultMessage: 'Итог замера', description: 'Подпись списка выбора итога повторного замера' },
  followupSaved: { id: 'quarantine.entry.followupSaved', defaultMessage: 'Замер записан. «Устранено» — только по замеру инспектора.', description: 'Сообщение после записи замера: устранено подтверждает только замер инспектора' },
  whyDecision: { id: 'quarantine.entry.whyDecision', defaultMessage: 'Почему к решению: {why}.', description: 'Строка в форме решения: причина, по которой случай ждёт решения; {why} — причина словами (строчными)' },
  decisionField: { id: 'quarantine.entry.decisionField', defaultMessage: 'Решение', description: 'Подпись списка выбора решения (красный / без красного / снять красный)' },
  reasonField: { id: 'quarantine.entry.reasonField', defaultMessage: 'Основание', description: 'Подпись поля ввода основания решения или направления' },
  newDeadlineField: { id: 'quarantine.entry.newDeadlineField', defaultMessage: 'Новый срок', description: 'Подпись поля даты нового срока при решении «оставить без красного»' },
  redUnavailable: { id: 'quarantine.entry.redUnavailable', defaultMessage: 'Красный недоступен: {class} — не карантинный объект.', description: 'Пояснение в форме решения: красный нельзя поставить; {class} — класс вида словами или «вид без строки справочника»' },
  noCatalogRow: { id: 'quarantine.entry.noCatalogRow', defaultMessage: 'вид без строки справочника', description: 'Вместо класса вида, когда вида нет в справочнике; строчными' },
  decisionSaved: { id: 'quarantine.entry.decisionSaved', defaultMessage: 'Решение записано.', description: 'Сообщение после записи решения' },
  referralNote: { id: 'quarantine.entry.referralNote', defaultMessage: 'Направление возможно только по красной единице земли.', description: 'Пояснение в форме направления в агроинспекцию' },
  referralSaved: { id: 'quarantine.entry.referralSaved', defaultMessage: 'Направление в агроинспекцию записано.', description: 'Сообщение после записи направления в агроинспекцию' },
});

const DIRECTIONS: Array<[string, Message]> = [
  ['pests', m.dirPests],
  ['diseases', m.dirDiseases],
  ['weeds', m.dirWeeds],
];
const OUTCOMES: Array<[string, Message]> = [
  ['eliminated', m.outEliminated],
  ['reduced', m.outReduced],
  ['unchanged', m.outUnchanged],
  ['increased', m.outIncreased],
];
const DECISIONS: Array<[string, Message]> = [
  ['set_red', m.decSetRed],
  ['keep', m.decKeep],
  ['lift_red', m.decLiftRed],
];

async function post(path: string, body: unknown): Promise<unknown> {
  await api.csrf();
  return request(path, undefined, { method: 'POST', body: JSON.stringify(body) });
}

/** Число из поля: запятая или точка; пусто или не число — `null`. */
export function parseNumber(text: string | undefined): number | null {
  if (!text) return null;
  const value = Number(text.trim().replace(',', '.'));
  return Number.isFinite(value) ? value : null;
}

/** Гектары поля ввода → микрогектары для сервера; пусто/ноль — `null`. */
export function haToMicroha(text: string | undefined): number | null {
  const value = parseNumber(text);
  return value !== null && value > 0 ? Math.round(value * 1_000_000) : null;
}

export function QuarantineEntry({
  contourId,
  parts,
  findings,
  requirements,
  onDone,
}: {
  contourId: number;
  parts: Part[];
  findings: EntryFinding[];
  requirements: Requirement[];
  onDone: () => void;
}) {
  const t = useT();
  const { principal } = useAuth();
  // Дата события — по умолчанию «сегодня по часам, но не позже среза»; позже среза события не бывает.
  const slice = useSliceDate();
  const today = (): string => clampToSlice(slice) ?? localIsoDate();
  const maxDay = slice ?? undefined;
  const [form, setForm] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [v, setV] = useState<Record<string, string>>({});
  const [problems, setProblems] = useState<QuarantineProblems | null>(null);
  const canEnter = principal?.can_enter_quarantine === true;
  const canDecide = principal?.can_decide_quarantine === true;

  // Справочник видов нужен только форме обследования.
  useEffect(() => {
    if (form !== 'inspection' || problems) return;
    let alive = true;
    api.quarantineProblems().then(
      (reply) => alive && setProblems(reply),
      () => undefined,
    );
    return () => {
      alive = false;
    };
  }, [form, problems]);

  // Находка по умолчанию — та, что ждёт решения, иначе последняя.
  const defaultFinding = findings.find((f) => f.awaiting_reason) ?? findings[0];
  const finding = findings.find((f) => String(f.id) === v.finding) ?? defaultFinding;
  const requirement = useMemo(
    () => [...requirements].reverse().find((r) => r.finding_id === finding?.id),
    [requirements, finding?.id],
  );
  if (!canEnter && !canDecide) return null;

  // Значения полей остаются строками в `v`: компоненты отдают значение, а не событие.
  const put = (key: string) => (value: string) => setV((prev) => ({ ...prev, [key]: value }));
  // Дата очищена — пустая строка, как у нативного поля раньше.
  const putDate = (key: string) => (iso: string | null) => put(key)(iso ?? '');
  const putNumber = (key: string) => (num: number | null) => put(key)(num === null ? '' : String(num));

  const submit = async (path: string, body: unknown, done: string) => {
    setBusy(true);
    setMessage(null);
    try {
      await post(path, body);
      setMessage(done);
      setForm(null);
      setV({});
      onDone();
    } catch (cause) {
      setMessage(t(m.saveFailed, { reason: cause instanceof ApiError ? cause.message : String(cause) }));
    } finally {
      setBusy(false);
    }
  };

  const problem = problems?.items.find((p) => String(p.id) === v.problem) ?? null;
  const share = parseNumber(v.share);
  const areaMicroha = haToMicroha(v.area);
  const findingClass = (finding?.object_class ?? null) as ObjectClass | null;
  const redAllowed = isQuarantineClass(findingClass);
  const decisionKind = v.kind ?? (finding?.awaiting_reason === 'ready_to_lift' ? 'lift_red' : redAllowed ? 'set_red' : 'keep');

  const forms: Array<[string, string, boolean]> = [
    ['inspection', t(m.formInspection), canEnter],
    ['requirement', t(m.formRequirement), canEnter && Boolean(finding)],
    ['followup', t(m.formFollowup), canEnter && Boolean(requirement)],
    ['decision', t(m.formDecision), canDecide && Boolean(finding)],
    ['referral', t(m.formReferral), canDecide && Boolean(finding)],
  ];
  const needsFinding = form && form !== 'inspection' && findings.length > 1;

  return (
    <div className="qentry">
      <div className="astra-situation-links">
        {forms
          .filter(([, , allowed]) => allowed)
          .map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`astra-button ${form === id ? 'astra-button-primary' : ''}`}
              aria-pressed={form === id}
              onClick={() => setForm(form === id ? null : id)}
            >
              {label}
            </button>
          ))}
      </div>

      {needsFinding ? (
        <div className="qentry__form">
          <Select
            label={t(m.byFinding)}
            value={String(finding?.id ?? '')}
            onChange={put('finding')}
            options={findings.map((f) => ({
              id: String(f.id),
              name: t(m.findingOption, { name: f.problem_name ?? t(m.speciesUnnamed), id: f.id, awaiting: f.awaiting_reason ? 'yes' : 'no' }),
            }))}
          />
        </div>
      ) : null}

      {form === 'inspection' ? (
        <div className="qentry__form">
          <DatePicker label={t(m.date)} value={(v.date ?? today()) || null} onChange={putDate('date')} maxValue={maxDay} />
          <Select
            label={t(m.contourPart)}
            value={v.part ?? ''}
            onChange={put('part')}
            options={[{ id: '', name: t(m.wholeContour) }, ...parts.map((p) => ({ id: String(p.id), name: p.label }))]}
          />
          <Select
            label={t(m.speciesField)}
            value={v.problem ?? ''}
            onChange={put('problem')}
            options={[
              { id: '', name: t(m.nothingFound) },
              ...(problems?.items ?? []).map((p) => ({ id: String(p.id), name: t(m.speciesWithClass, { name: p.name, class: CLASS_LONG[p.object_class] }) })),
              { id: 'other', name: t(m.otherSpecies) },
            ]}
          />
          {v.problem === 'other' ? (
            <>
              <Select
                label={t(m.directionField)}
                value={v.direction ?? 'pests'}
                onChange={put('direction')}
                options={DIRECTIONS.map(([id, label]) => ({ id, name: t(label) }))}
              />
              <TextField label={t(m.speciesText)} value={v.label ?? ''} onChange={put('label')} placeholder={t(m.speciesTextPlaceholder)} />
            </>
          ) : null}
          {v.problem ? (
            <>
              <NumberField
                label={t(m.affectedHa)}
                value={parseNumber(v.area)}
                onChange={putNumber('area')}
                unit={t(UNIT_LABELS.ha)}
                fractionDigits={6}
                placeholder={t(m.affectedHaPlaceholder)}
              />
              <NumberField
                label={t(m.affectedShare)}
                value={parseNumber(v.share)}
                onChange={putNumber('share')}
                unit="%"
                placeholder={t(m.affectedSharePlaceholder)}
              />
              <TextField label={t(m.observed)} value={v.note ?? ''} onChange={put('note')} />
              {problem && !isQuarantineClass(problem.object_class) ? (
                <p className="astra-footnote">
                  {t(m.classNote, { class: CLASS_LONG[problem.object_class] })}
                </p>
              ) : null}
              {!areaMicroha && !(share && share > 0 && share <= 100) ? (
                <p className="astra-footnote">{t(m.areaNote)}</p>
              ) : null}
            </>
          ) : null}
          <button
            type="button"
            className="astra-button astra-button-primary"
            disabled={busy || (v.problem === 'other' && !v.label)}
            onClick={() =>
              void submit(
                '/quarantine/inspections',
                {
                  contour_id: contourId,
                  contour_part_id: v.part ? Number(v.part) : null,
                  occurred_on: v.date ?? today(),
                  finding: v.problem
                    ? {
                        direction: v.problem === 'other' ? (v.direction ?? 'pests') : problem?.direction ?? 'pests',
                        problem_id: v.problem === 'other' ? null : Number(v.problem),
                        problem_label: v.problem === 'other' ? v.label || null : null,
                        note: v.note ?? '',
                        affected_area_microha: areaMicroha,
                        affected_share: !areaMicroha && share && share > 0 && share <= 100 ? share / 100 : null,
                      }
                    : null,
                },
                v.problem ? t(m.findingSaved) : t(m.inspectionSaved),
              )
            }
          >
            {busy ? t(m.saving) : t(m.save)}
          </button>
        </div>
      ) : null}

      {form === 'requirement' && finding ? (
        <div className="qentry__form">
          <p className="astra-footnote">{t(m.byFindingNote, { name: finding.problem_name ?? t(m.speciesUnnamed) })}</p>
          <TextField label={t(m.method)} value={v.method ?? ''} onChange={put('method')} />
          <DatePicker label={t(m.issued)} value={(v.issued ?? today()) || null} onChange={putDate('issued')} maxValue={maxDay} />
          <DatePicker
            label={t(m.deadline)}
            value={v.deadline || null}
            onChange={putDate('deadline')}
            minValue={(v.issued ?? today()) || undefined}
          />
          <button
            type="button"
            className="astra-button astra-button-primary"
            disabled={busy || !v.method}
            onClick={() =>
              void submit(
                `/quarantine/findings/${finding.id}/requirements`,
                { method: v.method, issued_on: v.issued ?? today(), deadline: v.deadline || null },
                t(m.requirementSaved),
              )
            }
          >
            {busy ? t(m.saving) : t(m.save)}
          </button>
        </div>
      ) : null}

      {form === 'followup' && requirement ? (
        <div className="qentry__form">
          <Select
            label={t(m.outcomeField)}
            value={v.outcome ?? 'eliminated'}
            onChange={put('outcome')}
            options={OUTCOMES.map(([id, label]) => ({ id, name: t(label) }))}
          />
          <DatePicker label={t(m.date)} value={(v.date ?? today()) || null} onChange={putDate('date')} maxValue={maxDay} />
          <button
            type="button"
            className="astra-button astra-button-primary"
            disabled={busy}
            onClick={() =>
              void submit(
                `/quarantine/requirements/${requirement.id}/followups`,
                { outcome: v.outcome ?? 'eliminated', occurred_on: v.date ?? today(), note: '' },
                t(m.followupSaved),
              )
            }
          >
            {busy ? t(m.saving) : t(m.save)}
          </button>
        </div>
      ) : null}

      {form === 'decision' && finding ? (
        <div className="qentry__form">
          {finding.awaiting_reason ? (
            <p className="astra-footnote">{t(m.whyDecision, { why: AWAITING_WORD[finding.awaiting_reason] ?? finding.awaiting_reason })}</p>
          ) : null}
          <Select
            label={t(m.decisionField)}
            value={decisionKind}
            onChange={put('kind')}
            options={DECISIONS.map(([id, label]) => ({ id, name: t(label), isDisabled: id === 'set_red' && !redAllowed }))}
          />
          <DatePicker label={t(m.date)} value={(v.date ?? today()) || null} onChange={putDate('date')} maxValue={maxDay} />
          <TextField label={t(m.reasonField)} value={v.reason ?? ''} onChange={put('reason')} />
          {decisionKind === 'keep' ? (
            <DatePicker label={t(m.newDeadlineField)} value={v.deadline || null} onChange={putDate('deadline')} />
          ) : null}
          {!redAllowed ? (
            <p className="astra-footnote">
              {t(m.redUnavailable, { class: findingClass ? CLASS_LONG[findingClass] : t(m.noCatalogRow) })}
            </p>
          ) : null}
          <button
            type="button"
            className="astra-button astra-button-primary"
            disabled={busy || !v.reason}
            onClick={() =>
              void submit(
                `/quarantine/findings/${finding.id}/decisions`,
                {
                  kind: decisionKind,
                  decided_on: v.date ?? today(),
                  reason: v.reason ?? '',
                  new_deadline: decisionKind === 'keep' && v.deadline ? v.deadline : null,
                },
                t(m.decisionSaved),
              )
            }
          >
            {busy ? t(m.saving) : t(m.save)}
          </button>
        </div>
      ) : null}

      {form === 'referral' && finding ? (
        <div className="qentry__form">
          <DatePicker label={t(m.date)} value={(v.date ?? today()) || null} onChange={putDate('date')} maxValue={maxDay} />
          <TextField label={t(m.reasonField)} value={v.basis ?? ''} onChange={put('basis')} />
          <p className="astra-footnote">{t(m.referralNote)}</p>
          <button
            type="button"
            className="astra-button astra-button-primary"
            disabled={busy || !v.basis}
            onClick={() =>
              void submit(
                `/quarantine/findings/${finding.id}/referrals`,
                { referred_on: v.date ?? today(), basis: v.basis },
                t(m.referralSaved),
              )
            }
          >
            {busy ? t(m.saving) : t(m.save)}
          </button>
        </div>
      ) : null}

      {message ? <p className="astra-footnote">{message}</p> : null}
    </div>
  );
}
